import { toast as sonnerToast } from 'sonner';

/**
 * Platform toast wrapper. Wraps `sonner` so the rest of the codebase
 * imports a single typed surface and never reaches into sonner's API
 * directly — that way the underlying library can be swapped without
 * touching every call site.
 *
 * Variants map to the same theme tokens the inline status banners
 * use (`--color-success`, `--color-critical`, `--color-warning`,
 * `--color-accent`) so toasts feel native against both daylight + midnight
 * themes; the `<Toaster />` mounted in AppShell pulls these via
 * `--toast-*` CSS variables it sets per-variant.
 *
 * SCOPE: the `<Toaster />` mount lives inside `AppShell`, which is the
 * `authed` route's component (see `router.tsx`). Calls from
 * unauthenticated routes (`/sign-in`, `/onboarding/*`) silently no-op
 * because there's no Toaster in the tree to render into. If you need
 * a toast on an unauth surface, add a second `<Toaster />` at that
 * route's component or hoist the existing one above the auth gate.
 */

interface ToastOptions {
  /** Persistent description below the title. */
  description?: string;
  /** Optional inline action — { label, onClick }. */
  action?: { label: string; onClick: () => void };
  /** ms — defaults to 5000 for info/success, 8000 for warning/error. */
  durationMs?: number;
}

export const toast = {
  success(title: string, opts?: ToastOptions): void {
    sonnerToast.success(title, {
      description: opts?.description,
      action: opts?.action,
      duration: opts?.durationMs ?? 5_000,
    });
  },
  error(title: string, opts?: ToastOptions): void {
    sonnerToast.error(title, {
      description: opts?.description,
      action: opts?.action,
      duration: opts?.durationMs ?? 8_000,
    });
  },
  warning(title: string, opts?: ToastOptions): void {
    sonnerToast.warning(title, {
      description: opts?.description,
      action: opts?.action,
      duration: opts?.durationMs ?? 8_000,
    });
  },
  info(title: string, opts?: ToastOptions): void {
    sonnerToast(title, {
      description: opts?.description,
      action: opts?.action,
      duration: opts?.durationMs ?? 5_000,
    });
  },
  /**
   * Agent variant — accent-tone card with the ◆ glyph prepended to
   * the title. Mirrors the redesign-v6 "◆ Agent is drafting…" card
   * (artboard `ov-toast` in `screens7.jsx`). Used for ephemeral agent
   * progress / draft notifications.
   */
  agent(title: string, opts?: ToastOptions): void {
    sonnerToast(`◆ ${title}`, {
      description: opts?.description,
      action: opts?.action,
      duration: opts?.durationMs ?? 5_000,
      className: 'border-s-[var(--color-accent)]',
    });
  },
  /** Programmatically dismiss a toast or all toasts (no arg). */
  dismiss: sonnerToast.dismiss,
};
